import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { MainNavigation } from '../components/navigation/MainNavigation'
import { MainFooter } from '../components/navigation/MainFooter'
import { useAuthStore } from '../stores/authStore'
import { NAVIGATION_ACTIONS } from '../config/navigation.config'
import { 
  AlertTriangle, 
  DollarSign, 
  Clock, 
  Database, 
  Timer,
  Target,
  BarChart3,
  CheckCircle,
  ArrowRight
} from 'lucide-react'

export const IntelligenceSolutionsPage: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()

  const handleGetStarted = () => {
    navigate(NAVIGATION_ACTIONS.primary.path)
  }

  const problems = [
    {
      id: 1,
      icon: <AlertTriangle className="h-6 w-6 text-white" />,
      title: "Flying Blind in Strategic Decisions",
      subtitle: "Making Critical Moves Without Intelligence",
      beforeTitle: "Without Strategic Intelligence",
      beforePoints: [
        "\"Should we expand to the West Coast?\" → CEO guesses based on gut feeling",
        "\"Are we pricing correctly?\" → CEO looks at 2-3 competitors online",
        "\"What's our biggest growth opportunity?\" → CEO has no systematic analysis",
        "Critical decisions made with incomplete information",
        "Reactive instead of proactive strategy"
      ],
      afterTitle: "With StratIQX Intelligence",
      afterPoints: [
        "Comprehensive market analysis with data-backed expansion recommendations",
        "Detailed competitive pricing analysis with optimization strategies", 
        "Systematic identification and prioritization of all growth opportunities",
        "Real-time market intelligence and trend analysis",
        "Proactive strategic positioning based on predictive insights"
      ]
    },
    {
      id: 2,
      icon: <DollarSign className="h-6 w-6 text-white" />,
      title: "The Expensive Consultant Dilemma",
      subtitle: "Quality Analysis at Impossible Costs",
      beforeTitle: "Typical Scenario",
      beforePoints: [
        "Business owner recognizes need for strategic help",
        "Calls Big 3 consulting firm → \"$400,000 for 4-month engagement\"",
        "Calls local consultant → \"$80,000 for basic analysis\"",
        "Result: Does nothing, makes uninformed decisions",
        "Strategic paralysis due to cost barriers"
      ],
      afterTitle: "With StratIQX Intelligence", 
      afterPoints: [
        "Gets Big 3 consulting quality for $15,000 in 45 minutes",
        "Can afford quarterly strategic reviews instead of one-time engagements",
        "Makes informed, data-backed decisions consistently",
        "Scales strategic intelligence without scaling costs",
        "Access to enterprise-grade analysis for mid-market budgets"
      ]
    },
    {
      id: 3,
      icon: <Clock className="h-6 w-6 text-white" />,
      title: "The CEO Strategy Bottleneck", 
      subtitle: "Leadership Time Trapped in Analysis",
      beforeTitle: "Current Reality",
      beforePoints: [
        "CEO spends 60% of time answering strategy questions",
        "Limited personal expertise in financial modeling, market analysis, competitive intelligence",
        "Analysis takes weeks or months of valuable CEO time",
        "Often results in incomplete or unconsciously biased analysis",
        "Strategic paralysis while CEO researches decisions"
      ],
      afterTitle: "With StratIQX Intelligence",
      afterPoints: [
        "CEO receives professional-grade analysis in under 1 hour",
        "Frees up 60% of CEO time for execution and leadership",
        "Unbiased, comprehensive analysis across all business dimensions",
        "Consistent analytical framework for all strategic decisions",
        "CEO becomes strategy executor, not strategy researcher"
      ]
    },
    {
      id: 4,
      icon: <Database className="h-6 w-6 text-white" />,
      title: "Information Overload Paralysis",
      subtitle: "Drowning in Data, Starving for Insights", 
      beforeTitle: "Without Strategic Intelligence",
      beforePoints: [
        "Drowning in data but starving for insights",
        "Multiple conflicting reports from different sources",
        "No unified view of market landscape",
        "Analysis paralysis from too many variables",
        "Key insights buried in data noise"
      ],
      afterTitle: "With StratIQX Intelligence",
      afterPoints: [
        "Synthesizes multiple data sources into actionable intelligence",
        "Presents clear, prioritized recommendations with confidence scores", 
        "Filters signal from noise across market, competitive, and internal data",
        "Provides executive summaries with drill-down capabilities",
        "Transforms information overload into strategic clarity"
      ]
    },
    {
      id: 5,
      icon: <Timer className="h-6 w-6 text-white" />,
      title: "Timing and Market Opportunity Blindness",
      subtitle: "Missing Windows While Competitors Move",
      beforeTitle: "Current Challenge", 
      beforePoints: [
        "Missing market windows due to slow analysis cycles",
        "Competitors move faster with better market intelligence", 
        "No early warning system for market shifts",
        "Strategic opportunities discovered too late",
        "Reactive positioning in rapidly changing markets"
      ],
      afterTitle: "With StratIQX Intelligence",
      afterPoints: [
        "Real-time market monitoring with automated alerts",
        "Predictive analysis of market trends and competitive moves",
        "Early identification of strategic opportunities and threats", 
        "Rapid scenario planning for market changes",
        "Proactive market positioning before competitors react"
      ]
    }
  ]

  const stats = [
    { number: '45 min', label: 'vs Months' },
    { number: '$15K', label: 'vs $400K' },
    { number: '60%', label: 'CEO Time Saved' }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Navigation */}
      <MainNavigation variant="full" />

      {/* Hero Section */}
      <section className="relative py-12 px-4 text-center">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-transparent" />
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="mb-6">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 bg-gradient-to-r from-white to-blue-300 bg-clip-text text-transparent">
              🎯 Strategic Intelligence Solutions
            </h1>
            <p className="text-xl text-blue-200 mb-4 font-medium">
              Stop Flying Blind. Get Big 3 Consulting Quality Analysis in Minutes, Not Months.
            </p>
          </div>
          
          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-12 mb-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-cyan-400 mb-2">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-8 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="space-y-8">
            {problems.map((problem) => (
              <Card key={problem.id} className="bg-slate-800 border-blue-800/30 hover:border-cyan-500/50 transition-all duration-300 group overflow-hidden">
                <div className="bg-gradient-to-r from-amber-600 to-orange-500 px-6 py-4 text-center">
                  <div className="flex items-center justify-center mb-2">
                    <div className="bg-white/20 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                      {problem.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        Problem {problem.id}: "{problem.title}"
                      </h3>
                      <p className="text-amber-100 text-sm">
                        {problem.subtitle}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-0">
                  {/* Before Section */}
                  <div className="bg-red-50/5 border-r border-red-500/20 p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                      <h4 className="text-lg font-semibold text-red-400">
                        {problem.beforeTitle}
                      </h4>
                    </div>
                    <ul className="space-y-3">
                      {problem.beforePoints.map((point, index) => (
                        <li key={index} className="flex items-start text-gray-300 text-sm">
                          <div className="text-red-400 mr-3 mt-1 flex-shrink-0">✗</div>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* After Section */}
                  <div className="bg-green-50/5 p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-3 h-3 bg-green-400 rounded-full mr-3"></div>
                      <h4 className="text-lg font-semibold text-green-400">
                        {problem.afterTitle}
                      </h4>
                    </div>
                    <ul className="space-y-3">
                      {problem.afterPoints.map((point, index) => (
                        <li key={index} className="flex items-start text-gray-300 text-sm">
                          <div className="text-green-400 mr-3 mt-1 flex-shrink-0">✓</div>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-slate-800/50">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="bg-gradient-to-r from-slate-800 to-blue-900 border border-blue-700/50 rounded-2xl p-8 backdrop-blur-lg">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Transform Your Strategic Intelligence?
            </h2>
            <p className="text-lg text-gray-300 mb-6 max-w-2xl mx-auto leading-relaxed">
              Join forward-thinking leaders who've eliminated strategic guesswork and accelerated their decision-making with AI-powered intelligence.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                onClick={handleGetStarted}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-4 text-lg shadow-xl flex items-center"
              >
                Start Your Strategic Analysis
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              
              <div className="text-sm text-gray-400">
                <div className="flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-green-400 mr-2" />
                  45-minute delivery
                </div>
                <div className="flex items-center justify-center mt-1">
                  <CheckCircle className="h-4 w-4 text-green-400 mr-2" />
                  Big 3 consulting quality insights
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <MainFooter variant="full" />
    </div>
  )
}